import { useEffect, useRef, useState, useCallback } from "react";

import EnvConfig from "@/config/env";

export interface SsePage {
  routePath: string;
  name: string;
  category: string;
  isAuthProtected?: boolean;
  depth?: number;
}

/**
 * Dedicated SSE hook for AI Site Sync — directly manages EventSource and state.
 * Uses refs for all mutable data to avoid re-render interference with the connection.
 * Page updates are batched/throttled to avoid excessive re-renders during rapid crawls.
 */
export function useSiteSyncSse(generationId: string | null) {
  const [livePages, setLivePages] = useState<SsePage[]>([]);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [needsLoginUrl, setNeedsLoginUrl] = useState(false);

  const esRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectCount = useRef(0);
  const connectedAtRef = useRef(0);
  const isCompleteRef = useRef(false);
  const everConnectedRef = useRef(false);
  const generationIdRef = useRef(generationId);

  // Batching: accumulate pages in a ref, flush to state on a throttle
  const pendingPagesRef = useRef<SsePage[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const FLUSH_INTERVAL = 500; // ms — update UI at most every 500ms

  const flushPages = useCallback(() => {
    if (pendingPagesRef.current.length === 0) return;
    const batch = pendingPagesRef.current;
    pendingPagesRef.current = [];
    setLivePages((prev) => {
      const existing = new Set(prev.map((p) => p.routePath));
      const newPages = batch.filter((p) => !existing.has(p.routePath));
      if (newPages.length === 0) return prev;
      return [...prev, ...newPages];
    });
  }, []);

  // Keep ref in sync so callbacks can access latest value
  generationIdRef.current = generationId;

  const cleanup = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    // Flush any remaining pages
    flushPages();
    setIsConnected(false);
  }, [flushPages]);

  useEffect(() => {
    if (!generationId) {
      cleanup();
      return;
    }

    // Reset state for new connection
    setLivePages([]);
    setProgress(0);
    setStatusMessage(null);
    setIsComplete(false);
    setHasError(false);
    setNeedsLoginUrl(false);
    isCompleteRef.current = false;
    everConnectedRef.current = false;
    reconnectCount.current = 0;
    connectedAtRef.current = 0;
    pendingPagesRef.current = [];

    const connect = () => {
      // Don't reconnect if completed or generationId was cleared
      if (isCompleteRef.current || !generationIdRef.current) return;
      if (esRef.current && esRef.current.readyState !== EventSource.CLOSED) {
        return;
      }

      const sseUrl = `${EnvConfig.serverURL}/api/sse/ai-stream?generationId=${encodeURIComponent(generationId)}`;

      try {
        const es = new EventSource(sseUrl, { withCredentials: true });
        esRef.current = es;

        es.addEventListener("connected", () => {
          // Record when this connection became live. We only reset the backoff
          // counter once a connection has proven *stable* (see onerror) — resetting
          // here on every reconnect would let a flapping connection loop tightly and
          // hammer the server (and its DB pool) with reconnect requests.
          connectedAtRef.current = Date.now();
          everConnectedRef.current = true;
          setIsConnected(true);
        });

        es.addEventListener("status", (event) => {
          try {
            const payload = JSON.parse(event.data);
            // Progress only ever moves forward — a later status event carrying a
            // lower/omitted value must not drag the bar backwards.
            if (typeof payload.progress === "number") {
              setProgress((prev) => Math.max(prev, payload.progress));
            }
            setStatusMessage(payload.message || null);
            if (payload.needsLoginUrl) {
              setNeedsLoginUrl(true);
            }
          } catch {
            /* ignore */
          }
        });

        es.addEventListener("update", (event) => {
          try {
            const parsed = JSON.parse(event.data);
            // Per-page events carry the crawler's own progress. Read it here too so
            // the bar advances smoothly instead of sitting at 0% between the sparse
            // milestone "status" events.
            if (typeof parsed.progress === "number") {
              setProgress((prev) => Math.max(prev, parsed.progress));
            }
            if (parsed.type === "page_found" && parsed.routePath) {
              const page: SsePage = {
                routePath: parsed.routePath,
                name: parsed.name || parsed.routePath,
                category: parsed.category || "other",
                isAuthProtected: parsed.isAuthProtected,
                depth: parsed.depth,
              };
              // Batch page into pending buffer; flush on timer
              pendingPagesRef.current.push(page);
              if (!flushTimerRef.current) {
                flushTimerRef.current = setTimeout(() => {
                  flushTimerRef.current = null;
                  flushPages();
                }, FLUSH_INTERVAL);
              }
            }
          } catch {
            /* ignore */
          }
        });

        es.addEventListener("complete", () => {
          isCompleteRef.current = true;
          // Flush any remaining buffered pages before marking complete
          flushPages();
          setIsComplete(true);
          setProgress(100);
          es.close();
          esRef.current = null;
          setIsConnected(false);
        });

        es.addEventListener("error", (event) => {
          try {
            const msgEvent = event as MessageEvent;
            if (msgEvent.data) {
              const payload = JSON.parse(msgEvent.data);
              setStatusMessage(payload.message || "Sync error");
            }
          } catch {
            /* ignore */
          }
          // A server-sent error event is terminal — guard against any further
          // (re)connect attempts, then close.
          isCompleteRef.current = true;
          setHasError(true);
          es.close();
          esRef.current = null;
          setIsConnected(false);
        });

        es.onerror = () => {
          // Don't reconnect if already completed / terminated.
          if (isCompleteRef.current) return;

          // The browser only lands an EventSource in CLOSED state when the server
          // ended the stream deliberately — e.g. our 204 reconnect-flood/terminal
          // backstop, or any non-retryable HTTP status. The native auto-reconnect
          // is already suppressed in that case; if we manually reconnect here we'd
          // defeat the server's stop-signal and spin a tight loop. So: stop.
          if (es.readyState === EventSource.CLOSED) {
            esRef.current = null;
            setIsConnected(false);
            return;
          }

          // If the connection stayed up for a meaningful window, treat it as a
          // healthy session that simply dropped — reset the backoff. Otherwise it's
          // flapping, so let the backoff keep growing to avoid a reconnect storm.
          const STABLE_MS = 30000;
          if (
            connectedAtRef.current &&
            Date.now() - connectedAtRef.current > STABLE_MS
          ) {
            reconnectCount.current = 0;
          }
          connectedAtRef.current = 0;

          es.close();
          esRef.current = null;
          setIsConnected(false);

          // If we have *never* established a connection for this generation, the
          // server is almost certainly rejecting us (204) or unreachable — cap
          // these "never-established" retries hard so we don't loop forever. The
          // 3s status poll remains authoritative for the terminal outcome.
          const maxAttempts = everConnectedRef.current ? 30 : 5;
          if (reconnectCount.current < maxAttempts) {
            reconnectCount.current++;
            const delay = Math.min(
              1000 * Math.pow(1.5, reconnectCount.current),
              15000,
            );
            reconnectTimer.current = setTimeout(connect, delay);
          }
        };
      } catch {
        /* ignore */
      }
    };

    connect();

    return cleanup;
  }, [generationId, cleanup, flushPages]);

  return {
    livePages,
    progress,
    statusMessage,
    isConnected,
    isComplete,
    hasError,
    needsLoginUrl,
  };
}
